import React, {useEffect, useMemo, useState} from "react";
import {useIntl, FormattedMessage} from "react-intl";
import {X, Search, SlidersHorizontal, ChevronDown, ChevronRight} from "lucide-react";
import {ReactComponent as RefreshIcon} from "../../svg/refresh.svg";
import {ScrollablePopupBody, usePopup} from "../../UIElements";
import {getThemeColor} from "../../utils/topicUtils";
import DateRangeSelector from "./DateRangeSelector";

const DEFAULT_FILTERS = {
    dateFrom: null,
    dateTo: null,
    campaigns: [],
    modules: [],
    language: "all",
    successStatus: "all",
    stopPoint: "all",
    supportGiven: "all",
    requestSuggested: "all",
    needCreated: "all",
    escalationPending: "all",
    referralMade: "all",
    referralText: "",
    topics: [],
    topicLogic: "or",
    sentiment: "all",
    analyzedStatus: "all",
    feedbackAsked: "all",
    feedbackResponse: "all",
    feedbackText: "",
    feedbackNudge: "all",
}

function getActiveFilterCount(filters) {
    let count = 0
    // dateFrom / dateTo are the primary date control, not counted as "advanced filters"
    if (filters.campaigns.length > 0) count++
    if (filters.modules.length > 0) count++
    if (filters.language !== "all") count++
    if (filters.successStatus !== "all") count++
    if (filters.stopPoint !== "all") count++
    if (filters.supportGiven !== "all") count++
    if (filters.requestSuggested !== "all") count++
    if (filters.needCreated !== "all") count++
    if (filters.escalationPending !== "all") count++
    if (filters.referralMade !== "all") count++
    if (filters.referralText.trim()) count++
    if (filters.topics.length > 0) count++
    if (filters.sentiment !== "all") count++
    if (filters.analyzedStatus !== "all") count++
    if (filters.feedbackAsked !== "all") count++
    if (filters.feedbackResponse !== "all") count++
    if (filters.feedbackText.trim()) count++
    if (filters.feedbackNudge !== "all") count++
    return count
}

function isBlockedSession(session) {
    const a = session.llmanalysis
    return a && (a.error === "BLOCKED" || a.blocked === true)
}

// Date-range scope only — distinct from the full filter set so callers can tell
// "nothing in this period" apart from "filters excluded everything".
export function sessionMatchesDateRange(session, filters) {
    // Use `created` (when the conversation happened) for date filtering; fall back to `updated`
    const sessionDate = new Date(session.created || session.updated)
    if (filters.dateFrom && sessionDate < new Date(filters.dateFrom)) return false
    if (filters.dateTo && sessionDate > new Date(filters.dateTo + "T23:59:59")) return false
    return true
}

// Maps an end-sentiment / start-sentiment value to a coarse zone. Sentiment is a judgement
// call and the raw -2..2 number isn't precise, so bucketing compares zones, not raw values.
function sentimentZoneRank(value) {
    const n = parseInt(value)
    if (isNaN(n)) return null
    if (n <= -1) return 0 // negative
    if (n === 0) return 1 // neutral
    return 2 // positive
}

export function sessionMatchesFilters(session, filters, supportNeeds, surveyModules, surveyCampaignKey, needAttribution) {
    if (!sessionMatchesDateRange(session, filters)) return false

    // Campaign filter matches on the campaign grouping key, so selecting one campaign
    // catches every survey that belongs to it (falls back to the survey id).
    if (filters.campaigns.length > 0) {
        const campaignKey = surveyCampaignKey?.[session.survey] ?? session.survey
        if (!filters.campaigns.includes(campaignKey)) return false
    }
    if (filters.modules.length > 0) {
        const sessionModules = surveyModules?.[session.survey] || []
        if (!filters.modules.some(m => sessionModules.includes(m))) return false
    }

    const a = session.llmanalysis
    const blocked = isBlockedSession(session)

    if (filters.analyzedStatus === "analyzed" && (!a || blocked)) return false
    if (filters.analyzedStatus === "notAnalyzed" && a) return false
    if (filters.analyzedStatus === "blocked" && !blocked) return false

    // The filters below require a completed (non-blocked) analysis. An unanalyzed
    // or blocked session can never satisfy them, so exclude it when any are active.
    // (Without this, the analysis-dependent block is simply skipped for unanalyzed
    // sessions and they leak through, e.g. when filtering by "Successful".)
    const requiresAnalysis =
        filters.successStatus !== "all" ||
        filters.stopPoint !== "all" ||
        filters.supportGiven !== "all" ||
        filters.requestSuggested !== "all" ||
        filters.referralMade !== "all" ||
        filters.referralText.trim() !== "" ||
        filters.feedbackAsked !== "all" ||
        filters.feedbackResponse !== "all" ||
        filters.feedbackText.trim() !== "" ||
        filters.feedbackNudge !== "all" ||
        filters.topics.length > 0 ||
        filters.language !== "all" ||
        filters.sentiment !== "all"
    if (requiresAnalysis && (!a || blocked)) return false

    if (a && !blocked) {
        if (filters.successStatus === "successful" && a.conversation_success !== true) return false
        if (filters.successStatus === "failed" && a.conversation_success !== false) return false
        if (filters.successStatus === "uncertain" && a.conversation_success !== null) return false

        if (filters.stopPoint !== "all" && a.conversation_stop_point !== filters.stopPoint) return false

        if (filters.language !== "all" && a.language !== filters.language) return false

        if (filters.supportGiven === "yes" && !a.support_given) return false
        if (filters.supportGiven === "no" && a.support_given) return false

        if (filters.requestSuggested === "yes" && !a.support_request_suggested) return false
        if (filters.requestSuggested === "no" && a.support_request_suggested) return false

        const hasReferral = (a.referrals?.length || 0) > 0
        if (filters.referralMade === "yes" && !hasReferral) return false
        if (filters.referralMade === "no" && hasReferral) return false
        const referralQuery = filters.referralText.trim().toLowerCase()
        if (referralQuery && !(a.referrals || []).some(r => r?.toLowerCase().includes(referralQuery))) return false

        if (filters.feedbackAsked === "yes" && !a.feedback_asked) return false
        if (filters.feedbackAsked === "no" && a.feedback_asked) return false

        if (filters.feedbackResponse === "positive" && a.feedback_response !== "positive") return false
        if (filters.feedbackResponse === "negative" && a.feedback_response !== "negative") return false
        if (filters.feedbackResponse === "noResponse" && a.feedback_response !== null) return false

        const feedbackQuery = filters.feedbackText.trim().toLowerCase()
        if (feedbackQuery && !a.feedback_details?.toLowerCase().includes(feedbackQuery)) return false

        // "Feedback led to a conversation" — the nudge metric, derived from session_kind.
        if (filters.feedbackNudge === "yes" && a.session_kind !== "feedback_then_support") return false
        if (filters.feedbackNudge === "no" && a.session_kind !== "feedback_only") return false

        if (filters.topics.length > 0) {
            const sessionTopics = a.topics || []
            if (filters.topicLogic === "and") {
                if (!filters.topics.every(t => sessionTopics.includes(t))) return false
            } else {
                if (!filters.topics.some(t => sessionTopics.includes(t))) return false
            }
        }

        if (filters.sentiment !== "all") {
            const startRank = sentimentZoneRank(a.user_sentiment_start)
            const endRank = sentimentZoneRank(a.user_sentiment_end)
            // Can't classify a journey without both endpoints.
            if (startRank == null || endRank == null) return false
            if (filters.sentiment === "improved" && !(endRank > startRank)) return false
            if (filters.sentiment === "worsened" && !(endRank < startRank)) return false
            if (filters.sentiment === "stayedNegative" && !(startRank === 0 && endRank === 0)) return false
            if (filters.sentiment === "stayedPositive" && !(startRank === 2 && endRank === 2)) return false
        }
    }

    if (filters.needCreated !== "all") {
        // "Created" is per-session: the request is attributed to the conversation that produced it
        const createdHere = !!needAttribution?.has(session.id)
        if (filters.needCreated === "yes" && !createdHere) return false
        if (filters.needCreated === "no" && createdHere) return false
    }

    // Escalation pending: the bot suggested a support request but none exists yet.
    if (filters.escalationPending === "yes") {
        if (!a || blocked || a.support_request_suggested !== true) return false
        const hasNeed = supportNeeds?.some(
            sn => sn.contact === session.contact && sn.survey === session.survey
        )
        if (hasNeed) return false
    }

    return true
}

export function contactMatchesFilters(contact, filters, supportNeeds, surveyModules, surveyCampaignKey, needAttribution) {
    return contact.sessions.some(s => sessionMatchesFilters(s, filters, supportNeeds, surveyModules, surveyCampaignKey, needAttribution))
}

function ToggleFilter({label, value, onChange, options}) {
    return <div className="toggle-filter">
        <span className="toggle-filter-label">{label}</span>
        <select value={value} onChange={e => onChange(e.target.value)}>
            {options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
        </select>
    </div>
}

// Scrollable multi-select with a live text filter, used for the Campaigns,
// Modules and Language sections. Items are {value, label}; selection state and
// toggling are owned by the parent (mirrors the topic search/list pattern).
function SearchableCheckboxList({items, selected, onToggle, searchPlaceholder, emptyText}) {
    const [query, setQuery] = useState("")
    const q = query.trim().toLowerCase()
    const filtered = q ? items.filter(it => it.label.toLowerCase().includes(q)) : items

    return <div className="searchable-filter-list">
        <div className="searchable-filter-search">
            <input
                type="search"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
            />
        </div>
        <div className="filter-checkbox-list">
            {filtered.map(it => (
                <label key={it.value}>
                    <input
                        type="checkbox"
                        checked={selected.includes(it.value)}
                        onChange={() => onToggle(it.value)}
                    />
                    {it.label}
                </label>
            ))}
            {filtered.length === 0 && (
                <span className="empty-text">{emptyText}</span>
            )}
        </div>
    </div>
}

export default function FilterBar({
    filters,
    onFiltersChange,
    sessions,
    surveyNames,
    surveyCampaignKey,
    moduleOptions,
    topicLabels,
    topicThemeMap,
    searchQuery,
    onSearchChange,
    onRefresh,
    isRefreshing,
}) {
    const intl = useIntl()
    const {showPopup, hidePopup} = usePopup()
    const [displayLoading, setDisplayLoading] = useState(false)
    const activeCount = getActiveFilterCount(filters)

    useEffect(() => {
        if (isRefreshing) {
            setDisplayLoading(true)
        } else {
            const timeoutId = setTimeout(() => {
                setDisplayLoading(false)
            }, 1000)
            return () => clearTimeout(timeoutId)
        }
    }, [isRefreshing])

    const availableTopics = useMemo(() => {
        const topics = new Set()
        sessions.forEach(s => {
            s.llmanalysis?.topics?.forEach(t => topics.add(t))
        })
        return Array.from(topics).sort()
    }, [sessions])

    // One entry per campaign. Surveys sharing a campaign collapse to a single grouping key
    // (so the campaign is listed once); surveys without a campaign stay distinct under their
    // own id, labelled by their title/id. Entries are [key, label].
    const availableCampaigns = useMemo(() => {
        const byKey = new Map()
        sessions.forEach(s => {
            if (!s.survey) return
            const key = surveyCampaignKey?.[s.survey] ?? s.survey
            if (!byKey.has(key)) byKey.set(key, surveyNames?.[s.survey] || s.survey)
        })
        return Array.from(byKey.entries()).sort((a, b) => String(a[1]).localeCompare(String(b[1])))
    }, [sessions, surveyNames, surveyCampaignKey])

    const availableLanguages = useMemo(() => {
        const languages = new Set()
        sessions.forEach(s => {
            if (s.llmanalysis?.language) languages.add(s.llmanalysis.language)
        })
        return Array.from(languages).sort()
    }, [sessions])

    const clearAll = () => {
        // Keep the date range (it's a primary control); only clear advanced filters
        onFiltersChange({...DEFAULT_FILTERS, dateFrom: filters.dateFrom, dateTo: filters.dateTo})
    }

    const openAdvanced = () => showPopup(
        <AdvancedFiltersModal
            filters={filters}
            onFiltersChange={onFiltersChange}
            onClose={hidePopup}
            availableTopics={availableTopics}
            availableCampaigns={availableCampaigns}
            moduleOptions={moduleOptions}
            availableLanguages={availableLanguages}
            topicLabels={topicLabels}
            topicThemeMap={topicThemeMap}
            intl={intl}
        />
    )

    return <>
        <div className="toolbar-section">
            <div className="search-row">
                <input
                    type="search"
                    value={searchQuery}
                    onChange={e => onSearchChange(e.target.value)}
                    placeholder={intl.formatMessage({id: "analysis.searchConversations", defaultMessage: "Search conversations..."})}
                />
                <button
                    className={displayLoading ? "icon-button refresh loading" : "icon-button refresh"}
                    onClick={onRefresh}
                    title={intl.formatMessage({id: "analysis.refresh", defaultMessage: "Update conversations"})}
                >
                    <RefreshIcon />
                </button>
            </div>

            <div className="filter-bar">
                <DateRangeSelector
                    value={{dateFrom: filters.dateFrom, dateTo: filters.dateTo}}
                    onChange={({dateFrom, dateTo}) => onFiltersChange({...filters, dateFrom, dateTo})}
                />
                <div className={"filter-btn-group"}>
                    <button
                        className={`filter-btn ${activeCount > 0 && "active"}`}
                        onClick={openAdvanced}
                    >
                        <SlidersHorizontal size={16} />
                        <FormattedMessage id="analysis.filters" defaultMessage="Filters" />
                        {activeCount > 0 && (
                            <span className="filter-count">{activeCount}</span>
                        )}
                    </button>
                    {activeCount > 0 && (
                        <button className="clear-filters-btn" onClick={clearAll}>
                            <X size={16} />
                            <FormattedMessage id="analysis.clearFilters" defaultMessage="Clear filters" />
                        </button>
                    )}
                </div>
                </div>
        </div>
    </>
}

function AdvancedFiltersModal({filters, onFiltersChange, onClose, availableTopics, availableCampaigns, moduleOptions, availableLanguages, topicLabels, topicThemeMap, intl}) {
    const [localFilters, setLocalFilters] = useState({...filters})
    const [topicSearch, setTopicSearch] = useState("")
    const [expandedThemes, setExpandedThemes] = useState({})

    const optAll = {value: "all", label: intl.formatMessage({id: "filter.all", defaultMessage: "All"})}
    const optYes = {value: "yes", label: intl.formatMessage({id: "yes", defaultMessage: "Yes"})}
    const optNo = {value: "no", label: intl.formatMessage({id: "no", defaultMessage: "No"})}

    const update = (key, value) => setLocalFilters(prev => ({...prev, [key]: value}))

    const toggleArrayValue = (key, value) => {
        setLocalFilters(prev => ({
            ...prev,
            [key]: prev[key].includes(value)
                ? prev[key].filter(v => v !== value)
                : [...prev[key], value]
        }))
    }

    const confirm = () => {
        onFiltersChange(localFilters)
        onClose()
    }

    // Reset filters but keep the date range — it is the primary control, not an advanced filter.
    const clearAll = () => {
        setLocalFilters(prev => ({...DEFAULT_FILTERS, dateFrom: prev.dateFrom, dateTo: prev.dateTo}))
    }

    const languageLabel = (code) => {
        try {
            return new Intl.DisplayNames([intl.locale], {type: "language"}).of(code) || code.toUpperCase()
        } catch (e) {
            return code.toUpperCase()
        }
    }

    // Option lists for the analysis-derived dropdowns. defaultMessage keeps them readable
    // before translations are added.
    const resultOptions = [
        optAll,
        {value: "successful", label: intl.formatMessage({id: "analysis.successful", defaultMessage: "Successful"})},
        {value: "failed", label: intl.formatMessage({id: "analysis.failed", defaultMessage: "Failed"})},
        {value: "uncertain", label: intl.formatMessage({id: "analysis.uncertain", defaultMessage: "Uncertain"})},
    ]
    const stopPointOptions = [
        optAll,
        {value: "resolved", label: intl.formatMessage({id: "analysis.stopPoint.resolved", defaultMessage: "Resolved"})},
        {value: "referred", label: intl.formatMessage({id: "analysis.stopPoint.referred", defaultMessage: "Referred"})},
        {value: "user_disengaged", label: intl.formatMessage({id: "analysis.stopPoint.userDisengaged", defaultMessage: "Student disengaged"})},
        {value: "user_declined", label: intl.formatMessage({id: "analysis.stopPoint.userDeclined", defaultMessage: "Student declined"})},
        {value: "no_engagement", label: intl.formatMessage({id: "analysis.stopPoint.noEngagement", defaultMessage: "No engagement"})},
        {value: "bot_error", label: intl.formatMessage({id: "analysis.stopPoint.botError", defaultMessage: "Bot error"})},
        {value: "other", label: intl.formatMessage({id: "analysis.stopPoint.other", defaultMessage: "Other"})},
    ]
    const sentimentOptions = [
        optAll,
        {value: "improved", label: intl.formatMessage({id: "analysis.filter.sentimentImproved", defaultMessage: "Improved"})},
        {value: "worsened", label: intl.formatMessage({id: "analysis.filter.sentimentWorsened", defaultMessage: "Worsened"})},
        {value: "stayedNegative", label: intl.formatMessage({id: "analysis.filter.sentimentStayedNegative", defaultMessage: "Stayed negative"})},
        {value: "stayedPositive", label: intl.formatMessage({id: "analysis.filter.sentimentStayedPositive", defaultMessage: "Stayed positive"})},
    ]

    // Topics grouped by theme, mirroring the stats chart. Built from the topics actually
    // present in the loaded data; search narrows topics and auto-expands matching themes.
    const searchLower = topicSearch.trim().toLowerCase()
    const allTopicGroups = useMemo(() => {
        const groups = {}
        availableTopics.forEach(code => {
            const theme = topicThemeMap?.[code] || "99"
            if (!groups[theme]) {
                groups[theme] = {
                    theme,
                    label: intl.formatMessage({id: `theme.${theme}`, defaultMessage: theme}),
                    color: getThemeColor(theme),
                    topics: [],
                }
            }
            groups[theme].topics.push({code, label: topicLabels?.[code] || code})
        })
        return Object.values(groups)
            .map(g => ({...g, topics: g.topics.sort((a, b) => a.label.localeCompare(b.label))}))
            .sort((a, b) => a.label.localeCompare(b.label))
    }, [availableTopics, topicThemeMap, topicLabels, intl])

    const topicGroups = searchLower
        ? allTopicGroups
            .map(g => {
                const themeMatch = g.label.toLowerCase().includes(searchLower)
                const topics = themeMatch
                    ? g.topics
                    : g.topics.filter(t => t.label.toLowerCase().includes(searchLower) || t.code.toLowerCase().includes(searchLower))
                return {...g, topics}
            })
            .filter(g => g.topics.length > 0)
        : allTopicGroups

    const toggleTheme = (theme) => setExpandedThemes(prev => ({...prev, [theme]: !prev[theme]}))
    const isThemeOpen = (theme) => !!searchLower || !!expandedThemes[theme]

    const themeSelectedCount = (group) => group.topics.filter(t => localFilters.topics.includes(t.code)).length
    const toggleThemeSelection = (group) => {
        const codes = group.topics.map(t => t.code)
        const allSelected = themeSelectedCount(group) === codes.length
        setLocalFilters(prev => {
            if (allSelected) {
                return {...prev, topics: prev.topics.filter(c => !codes.includes(c))}
            }
            const set = new Set(prev.topics)
            codes.forEach(c => set.add(c))
            return {...prev, topics: Array.from(set)}
        })
    }

    return <div className="advanced-filters">
            <h3>
                <FormattedMessage id="analysis.advancedFilters" defaultMessage="Filters" />
            </h3>

            <ScrollablePopupBody footer={
                <div className="options modal-footer">
                    <button onClick={clearAll} className={"align-left"}>
                        <FormattedMessage id="analysis.clearAll" defaultMessage="Clear all" />
                    </button>
                    <button onClick={onClose}>
                        <FormattedMessage id="modal.cancel" defaultMessage="Cancel" />
                    </button>
                    <button className="confirm" onClick={confirm}>
                        <FormattedMessage id="analysis.confirm" defaultMessage="Confirm" />
                    </button>
                </div>
            }>
            {/* Section 1 — Scope */}
            <div className="filter-section">
                <div className="filter-grid">
                    <div>
                        <h4>
                            <FormattedMessage id="supportCampaigns" defaultMessage="Support Campaigns" />
                        </h4>
                        <SearchableCheckboxList
                            items={availableCampaigns.map(([key, name]) => ({value: key, label: name}))}
                            selected={localFilters.campaigns}
                            onToggle={v => toggleArrayValue("campaigns", v)}
                            searchPlaceholder={intl.formatMessage({id: "analysis.filter.searchCampaigns", defaultMessage: "Search campaigns..."})}
                            emptyText={intl.formatMessage({id: "analysis.filter.noCampaigns", defaultMessage: "No campaigns in loaded data"})}
                        />
                    </div>
                    {moduleOptions?.length > 0 && (
                        <div>
                            <h4>
                                <FormattedMessage id="supportBots" defaultMessage="Support Bot Modules" />
                            </h4>
                            <SearchableCheckboxList
                                items={moduleOptions.map(({key, name}) => ({value: key, label: name}))}
                                selected={localFilters.modules}
                                onToggle={v => toggleArrayValue("modules", v)}
                                searchPlaceholder={intl.formatMessage({id: "analysis.filter.searchModules", defaultMessage: "Search modules..."})}
                                emptyText={intl.formatMessage({id: "analysis.filter.noModules", defaultMessage: "No modules found"})}
                            />
                        </div>
                    )}
                </div>
            </div>

                <div className="filter-section">
                    <div className="filter-grid">
                        <ToggleFilter
                            label={intl.formatMessage({id: "analysis.filter.processing", defaultMessage: "Processing status"})}
                            value={localFilters.analyzedStatus}
                            onChange={v => update("analyzedStatus", v)}
                            options={[
                                optAll,
                                {value: "analyzed", label: intl.formatMessage({id: "analysis.filter.analyzed", defaultMessage: "Analyzed"})},
                                {value: "notAnalyzed", label: intl.formatMessage({id: "analysis.filter.notAnalyzed", defaultMessage: "Not analyzed"})},
                                {value: "blocked", label: intl.formatMessage({id: "analysis.blocked", defaultMessage: "Blocked"})},
                            ]}
                        />
                        {availableLanguages.length > 0 && (
                            <ToggleFilter
                                label={intl.formatMessage({id: "language", defaultMessage: "Language"})}
                                value={localFilters.language}
                                onChange={v => update("language", v)}
                                options={[
                                    optAll,
                                    ...availableLanguages.map(code => ({value: code, label: languageLabel(code)})),
                                ]}
                            />
                        )}
                    </div>
                </div>

            {/* Section 2 — Outcome */}
            <div className="filter-section">
                <h4>
                    <FormattedMessage id="analysis.filter.outcome" defaultMessage="Outcome" />
                </h4>
                <div className="filter-grid">
                    <ToggleFilter
                        label={intl.formatMessage({id: "analysis.filter.result", defaultMessage: "Result"})}
                        value={localFilters.successStatus}
                        onChange={v => update("successStatus", v)}
                        options={resultOptions}
                    />
                    <ToggleFilter
                        label={intl.formatMessage({id: "analysis.filter.stopPoint", defaultMessage: "How it ended"})}
                        value={localFilters.stopPoint}
                        onChange={v => update("stopPoint", v)}
                        options={stopPointOptions}
                    />
                    <ToggleFilter
                        label={intl.formatMessage({id: "analysis.sentiment", defaultMessage: "Message Tone"})}
                        value={localFilters.sentiment}
                        onChange={v => update("sentiment", v)}
                        options={sentimentOptions}
                    />
                    <ToggleFilter
                        label={intl.formatMessage({id: "analysis.supportGiven", defaultMessage: "Support given"})}
                        value={localFilters.supportGiven}
                        onChange={v => update("supportGiven", v)}
                        options={[optAll, optYes, optNo]}
                    />
                    <ToggleFilter
                        label={intl.formatMessage({id: "analysis.supportRequestSuggested", defaultMessage: "Support request suggested"})}
                        value={localFilters.requestSuggested}
                        onChange={v => update("requestSuggested", v)}
                        options={[optAll, optYes, optNo]}
                    />
                    <ToggleFilter
                        label={intl.formatMessage({id: "analysis.supportRequestCreated", defaultMessage: "Support request created"})}
                        value={localFilters.needCreated}
                        onChange={v => update("needCreated", v)}
                        options={[optAll, optYes, optNo]}
                    />
                    <ToggleFilter
                        label={intl.formatMessage({id: "analysis.filter.referralMade", defaultMessage: "Referral made"})}
                        value={localFilters.referralMade}
                        onChange={v => update("referralMade", v)}
                        options={[optAll, optYes, optNo]}
                    />
                    <div className="toggle-filter searchable-filter-search">
                        <span className={"toggle-filter-label"}>{intl.formatMessage({id: "analysis.filter.referralContains", defaultMessage: "Referral contains"})}</span>
                        <input
                            type="search"
                            value={localFilters.referralText}
                            onChange={e => update("referralText", e.target.value)}
                            placeholder={intl.formatMessage({id: "analysis.filter.referralContainsPlaceholder", defaultMessage: "e.g. study psychologist"})}
                        />
                    </div>
                </div>
            </div>

            {/* Section 3 — Feedback */}
            <div className="filter-section">
                <h4>
                    <FormattedMessage id="analysis.filter.feedback" defaultMessage="Feedback" />
                </h4>
                <div className="filter-grid">
                    <ToggleFilter
                        label={intl.formatMessage({id: "analysis.filter.feedbackAsked", defaultMessage: "Feedback asked"})}
                        value={localFilters.feedbackAsked}
                        onChange={v => update("feedbackAsked", v)}
                        options={[optAll, optYes, optNo]}
                    />
                    <ToggleFilter
                        label={intl.formatMessage({id: "analysis.filter.feedbackResponse", defaultMessage: "Feedback response"})}
                        value={localFilters.feedbackResponse}
                        onChange={v => update("feedbackResponse", v)}
                        options={[
                            optAll,
                            {value: "positive", label: intl.formatMessage({id: "analysis.feedback.positive", defaultMessage: "Positive"})},
                            {value: "negative", label: intl.formatMessage({id: "analysis.feedback.negative", defaultMessage: "Negative"})},
                            {value: "noResponse", label: intl.formatMessage({id: "analysis.filter.noResponse", defaultMessage: "No response"})},
                        ]}
                    />
                    <ToggleFilter
                        label={intl.formatMessage({id: "analysis.filter.feedbackNudge", defaultMessage: "Feedback led to a conversation"})}
                        value={localFilters.feedbackNudge}
                        onChange={v => update("feedbackNudge", v)}
                        options={[optAll, optYes, optNo]}
                    />
                    <div className="toggle-filter searchable-filter-search">
                        <span className={"toggle-filter-label"}>{intl.formatMessage({id: "analysis.filter.feedbackContains", defaultMessage: "Feedback contains"})}</span>
                        <input
                            type="search"
                            value={localFilters.feedbackText}
                            onChange={e => update("feedbackText", e.target.value)}
                            placeholder={intl.formatMessage({id: "analysis.filter.feedbackContainsPlaceholder", defaultMessage: "Search feedback text..."})}
                        />
                    </div>
                </div>
            </div>

            {/* Section 4 — Topics */}
            <div className="filter-section">
                <h4>
                    <FormattedMessage id="analysis.stats.conversationTopics" defaultMessage="Conversation Topics" />
                </h4>
                <div className="topic-search-row">
                    <div className="topic-search-input">
                        <input
                            type="search"
                            value={topicSearch}
                            onChange={e => setTopicSearch(e.target.value)}
                            placeholder={intl.formatMessage({id: "analysis.filter.searchTopics", defaultMessage: "Search topics..."})}
                        />
                    </div>
                    <select
                        value={localFilters.topicLogic}
                        onChange={e => update("topicLogic", e.target.value)}
                    >
                        <option value="or">{intl.formatMessage({id: "analysis.filter.topicLogicOr", defaultMessage: "OR"})}</option>
                        <option value="and">{intl.formatMessage({id: "analysis.filter.topicLogicAnd", defaultMessage: "AND"})}</option>
                    </select>
                </div>
                <div className="filter-checkbox-list topic-theme-list">
                    {topicGroups.map(group => {
                        const selectedCount = themeSelectedCount(group)
                        const open = isThemeOpen(group.theme)
                        return (
                            <div className="topic-theme-group" key={group.theme}>
                                <div className="topic-theme-header">
                                    <input
                                        type="checkbox"
                                        checked={selectedCount === group.topics.length && group.topics.length > 0}
                                        ref={el => { if (el) el.indeterminate = selectedCount > 0 && selectedCount < group.topics.length }}
                                        onChange={() => toggleThemeSelection(group)}
                                    />
                                    <span className="theme-dot" style={{backgroundColor: group.color}} />
                                    <button type="button" className="theme-toggle" onClick={() => toggleTheme(group.theme)}>
                                        <span className="theme-name">{group.label}</span>
                                        <span className="theme-count">
                                            {selectedCount > 0 ? `${selectedCount}/${group.topics.length}` : group.topics.length}
                                        </span>
                                        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                    </button>
                                </div>
                                {open && (
                                    <div className="topic-theme-topics">
                                        {group.topics.map(t => (
                                            <label key={t.code}>
                                                <input
                                                    type="checkbox"
                                                    checked={localFilters.topics.includes(t.code)}
                                                    onChange={() => toggleArrayValue("topics", t.code)}
                                                />
                                                {t.label}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                    {topicGroups.length === 0 && (
                        <span className="empty-text">
                            <FormattedMessage id="analysis.filter.noTopics" defaultMessage="No topics found" />
                        </span>
                    )}
                </div>
            </div>
            </ScrollablePopupBody>
        </div>
}

export {DEFAULT_FILTERS, getActiveFilterCount}
